import React from 'react';
import {
  Truck, ClipboardCheck, AlertTriangle, ShieldCheck,
  Search, Filter, Eye, Flag, CheckCircle2, XCircle, Clock, Download, FileDown,
} from 'lucide-react';
import type { InspectionSummary, FleetStats, Inspection } from '@/types';
import { flagInspection, getRecentInspections, reviewInspection, getFleetStats, getInspection, exportSubmissionsCsv, exportMissedCsv } from '@/services/api';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import { toast } from 'react-hot-toast';
import { exportInspectionPDF } from '@/services/pdfExport';
import { useRole } from '@/hooks/useRole';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { downloadBlob } from '@/utils/downloadBlob';
import { InspectionReports } from '@/components/admin/InspectionReports';

const PAGE_SIZE = 20;

function defaultExportRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toInput = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toInput(start), end: toInput(end) };
}

export function AdminDashboard() {
  const [stats, setStats] = React.useState<FleetStats | null>(null);
  const [inspections, setInspections] = React.useState<InspectionSummary[]>([]);
  const [filter, setFilter] = React.useState<string>('all');
  const [search, setSearch] = React.useState('');
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [selectedInspection, setSelectedInspection] = React.useState<Inspection | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [tab, setTab] = React.useState<'inspections' | 'reports'>('inspections');
  const [showExportPanel, setShowExportPanel] = React.useState(false);
  const [exportRange, setExportRange] = React.useState(defaultExportRange);
  const [exportKind, setExportKind] = React.useState<'submissions' | 'missed'>('submissions');
  const [exporting, setExporting] = React.useState(false);

  const { canApprove } = useRole();
  const user = useCurrentUser() as any;

  async function handleBulkExport() {
    setExporting(true);
    try {
      const start = `${exportRange.start}T00:00:00.000Z`;
      const end = `${exportRange.end}T23:59:59.999Z`;
      const blob = exportKind === 'submissions'
        ? await exportSubmissionsCsv({ start, end })
        : await exportMissedCsv({ start, end });
      const filename = `${exportKind === 'submissions' ? 'inspections' : 'missed-inspections'}-${exportRange.start}-to-${exportRange.end}.csv`;
      downloadBlob(blob, filename);
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Bulk export failed:', err);
      toast.error('Failed to export data.');
    } finally {
      setExporting(false);
    }
  }

  async function handleView(id: string) {
    setLoadingDetail(true);
    try {
      const data = await getInspection(id);
      setSelectedInspection(data);
    } catch (err) {
      console.error('Failed to load inspection:', err);
      toast.error('Failed to load inspection details.');
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleExport(id: string) {
    try {
      const data = await getInspection(id);
      await exportInspectionPDF(data);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export PDF.');
    }
  }

  async function loadData(pageNum = 0) {
    setLoading(true);
    try {
      const [inspectionData, statsData] = await Promise.all([
        getRecentInspections(PAGE_SIZE, pageNum * PAGE_SIZE),
        getFleetStats(),
      ]);
      setInspections(inspectionData.items);
      setTotal(inspectionData.total);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadData(page);
  }, [page]);

  const filtered = inspections.filter((i) => {
    const matchesFilter = filter === 'all' || i.status === filter;
    const matchesSearch =
      i.vehicleUnitNumber.toLowerCase().includes(search.toLowerCase()) ||
      i.inspectorName.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 dark:bg-gray-800 min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-wire3-navy dark:text-wire3-green">Fleet Inspection Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Overview of fleet compliance and recent inspections.</p>
        </div>
        {canApprove && (
          <div className="flex gap-2">
            <button
              onClick={() => setTab('inspections')}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === 'inspections'
                ? 'bg-wire3-navy text-white dark:bg-wire3-green dark:text-wire3-navy'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              Inspections
            </button>
            <button
              onClick={() => setTab('reports')}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === 'reports'
                ? 'bg-wire3-navy text-white dark:bg-wire3-green dark:text-wire3-navy'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              Reports
            </button>
          </div>
        )}
      </div>

      {tab === 'reports' && canApprove ? (
        <InspectionReports />
      ) : (
      <>
      {/* Stats cards */}
      {loading ? (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card dark:bg-gray-750 dark:border-gray-700 flex flex-col items-center text-center">
              <div className="h-5 w-5 rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
              <div className="mt-2 h-7 w-10 rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
              <div className="mt-1 h-3 w-16 rounded bg-gray-100 dark:bg-gray-700 animate-pulse" />
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatCard icon={<Truck />} label="Total Vehicles" value={stats.totalVehicles} />
          <StatCard icon={<ClipboardCheck />} label="Inspected Today" value={stats.inspectedToday} color="text-status-pass" />
          <StatCard icon={<Clock />} label="Overdue" value={stats.overdueInspections} color="text-status-warning" />
          <StatCard icon={<AlertTriangle />} label="Open Defects" value={stats.openDefects} color="text-status-fail" />
          <StatCard icon={<ShieldCheck />} label="Compliance" value={`${stats.complianceRate}%`} color="text-wire3-green" />
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 overflow-x-auto">
          <Filter className="h-4 w-4 shrink-0 text-gray-400" />
          {['all', 'submitted', 'reviewed', 'flagged'].map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(0); }}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${filter === f
                ? 'bg-wire3-navy text-white dark:bg-wire3-green dark:text-wire3-navy'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="input-field py-2 pl-9 text-sm"
            />
          </div>
          {canApprove && (
            <button
              onClick={() => setShowExportPanel(v => !v)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <FileDown className="h-4 w-4" />
              Export Data
            </button>
          )}
        </div>
      </div>

      {showExportPanel && canApprove && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4">
          <label className="text-xs text-gray-500 dark:text-gray-400">
            Start
            <input
              type="date"
              value={exportRange.start}
              onChange={(e) => setExportRange(r => ({ ...r, start: e.target.value }))}
              className="input-field mt-1 block py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">
            End
            <input
              type="date"
              value={exportRange.end}
              onChange={(e) => setExportRange(r => ({ ...r, end: e.target.value }))}
              className="input-field mt-1 block py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">
            Report
            <select
              value={exportKind}
              onChange={(e) => setExportKind(e.target.value as 'submissions' | 'missed')}
              className="input-field mt-1 block py-1.5 text-sm"
            >
              <option value="submissions">Submissions log (one row per inspection)</option>
              <option value="missed">Missed inspections (per-vehicle gaps)</option>
            </select>
          </label>
          <button
            onClick={handleBulkExport}
            disabled={exporting}
            className="btn-primary py-2 text-xs disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Download CSV'}
          </button>
        </div>
      )}

      {error && <p className="mt-4 text-center text-sm text-status-fail">Failed to load: {error}</p>}
      {loading && <LoadingSpinner message="Loading inspections..." />}

      {!loading && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Vehicle</th>
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Inspector</th>
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Type</th>
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Status</th>
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Fails</th>
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Submitted</th>
                <th className="pb-3 font-semibold text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((insp) => (
                <tr key={insp.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="py-3 font-semibold text-wire3-navy dark:text-wire3-green">{insp.vehicleUnitNumber}</td>
                  <td className="py-3 text-gray-700 dark:text-gray-300">{insp.inspectorName}</td>
                  <td className="py-3 capitalize text-gray-500 dark:text-gray-400">{insp.type.replace('-', ' ')}</td>
                  <td className="py-3"><StatusBadge status={insp.status} /></td>
                  <td className="py-3">
                    {insp.failCount > 0 ? (
                      <span className="font-semibold text-status-fail">{insp.failCount}</span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">0</span>
                    )}
                  </td>
                  <td className="py-3 text-gray-500 dark:text-gray-400">
                    {new Date(insp.submittedAt).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                    })}
                  </td>
                  <td className="py-3">
                    <div className="flex gap-1">
                      <button
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-wire3-navy dark:hover:text-wire3-green"
                        title="View"
                        onClick={() => handleView(insp.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-wire3-navy dark:hover:text-wire3-green"
                        title="Export PDF"
                        onClick={() => handleExport(insp.id)}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {canApprove && (
                        <button
                          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-status-pass"
                          title="Approve"
                          onClick={async () => {
                            try {
                              await reviewInspection(insp.id, user?.id, '');
                              toast.success('Inspection approved');
                              await loadData(page);
                            } catch {
                              toast.error('Failed to approve inspection');
                            }
                          }}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      )}
                      {canApprove && (
                        <button
                          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-status-fail"
                          title="Flag"
                          onClick={async () => {
                            try {
                              await flagInspection(insp.id, 'Flagged by supervisor');
                              toast.success('Inspection flagged');
                              await loadData(page);
                            } catch {
                              toast.error('Failed to flag inspection');
                            }
                          }}
                        >
                          <Flag className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <p className="py-8 text-center text-gray-400">No inspections match your filters.</p>
          )}

          {/* Pagination controls */}
          {total > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} inspections
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page + 1 >= totalPages}
                  className="rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {stats && import.meta.env.DEV && (
            <button
              onClick={() => fetch('/api/notifications/check-overdue', { method: 'POST' })}
              className="btn-secondary mt-4 text-xs"
            >
              Test Overdue Check
            </button>
          )}
        </div>
      )}
      </>
      )}

      {loadingDetail && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="rounded-xl bg-white dark:bg-gray-800 px-6 py-5 shadow-xl flex items-center gap-3">
            <LoadingSpinner size="sm" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Loading inspection...</p>
          </div>
        </div>
      )}

      {selectedInspection && (
        <InspectionDetailModal
          inspection={selectedInspection}
          onClose={() => setSelectedInspection(null)}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }: {
  icon: React.ReactNode; label: string; value: string | number; color?: string;
}) {
  return (
    <div className="card dark:bg-gray-750 dark:border-gray-700 flex flex-col items-center text-center">
      <div className={`h-5 w-5 ${color || 'text-gray-400'}`}>{icon}</div>
      <p className={`mt-2 text-2xl font-bold ${color || 'text-wire3-navy dark:text-gray-100'}`}>{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    submitted: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    reviewed: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    flagged: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    'in-progress': 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
  };
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${styles[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
      {status}
    </span>
  );
}

function InspectionDetailModal({ inspection, onClose }: { inspection: Inspection; onClose: () => void }) {
  const allChecks = inspection.categories.flatMap(cat =>
    cat.checks.map(check => ({ ...check, categoryName: cat.name }))
  );
  const failed = allChecks.filter(c => c.status === 'fail');
  const passed = allChecks.filter(c => c.status === 'pass');
  const naChecks = allChecks.filter(c => c.status === 'na');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-800 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-wire3-navy dark:text-wire3-green">
              Inspection — {inspection.vehicleUnitNumber}
            </h2>
            <p className="text-sm text-gray-400 capitalize">
              {inspection.type.replace('-', ' ')} · {new Date(inspection.submittedAt ?? '').toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportInspectionPDF(inspection)}
              className="flex items-center gap-1.5 rounded-lg bg-wire3-navy dark:bg-wire3-green px-3 py-1.5 text-xs font-semibold text-white dark:text-wire3-navy hover:opacity-90"
            >
              <Download className="h-3.5 w-3.5" />
              Export PDF
            </button>
            <button onClick={onClose} className="rounded-full p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 flex flex-col gap-6">
          {inspection.status === 'flagged' && (
            <div className="rounded-lg border border-status-fail/30 bg-status-fail/5 dark:bg-status-fail/10 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-status-fail">
                <Flag className="h-4 w-4" />
                Flagged{inspection.inspectorFlagged ? ' by inspector' : ''}
              </div>
              {inspection.flagReason && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{inspection.flagReason}</p>
              )}
            </div>
          )}

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Details</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Vehicle', value: inspection.vehicleUnitNumber },
                { label: 'Inspector', value: inspection.inspectorName },
                { label: 'Type', value: inspection.type.replace('-', ' ') },
                { label: 'Status', value: <StatusBadge status={inspection.status} /> },
                { label: 'Mileage', value: inspection.mileage ? `${inspection.mileage.toLocaleString()} mi` : '—' },
                { label: 'Started', value: new Date(inspection.startedAt).toLocaleString() },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg bg-gray-50 dark:bg-gray-700 px-4 py-3">
                  <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-800 dark:text-gray-100 capitalize">{value}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Results</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-status-pass/10 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-status-pass">{passed.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Passed</p>
              </div>
              <div className="rounded-lg bg-status-fail/10 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-status-fail">{failed.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Failed</p>
              </div>
              <div className="rounded-lg bg-gray-100 dark:bg-gray-700 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-gray-400">{naChecks.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">N/A</p>
              </div>
            </div>
          </section>

          {failed.length > 0 && (
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-status-fail">Failed Items</h3>
              <div className="flex flex-col gap-3">
                {failed.map(check => (
                  <div key={check.id} className="rounded-lg border border-status-fail/20 bg-status-fail/5 dark:bg-status-fail/10 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{check.label}</p>
                        <p className="text-xs text-gray-400">{check.categoryName}</p>
                      </div>
                      <XCircle className="h-4 w-4 shrink-0 text-status-fail" />
                    </div>
                    {check.aiReasoning && (
                      <div className="mt-2 rounded-lg bg-status-fail/5 border border-status-fail/10 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-status-fail mb-0.5">AI Analysis</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 italic">{check.aiReasoning}</p>
                      </div>
                    )}
                    {check.notes && (
                      <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">"{check.notes}"</p>
                    )}
                    {check.photos.length > 0 && (
                      <div className="mt-3 flex flex-col gap-3">
                        {check.photos.map(photo => (
                          <div key={photo.id} className="flex gap-3">
                            <div className="relative shrink-0">
                              <img
                                src={photo.uri}
                                alt="Inspection photo"
                                className={`h-24 w-24 rounded-lg object-cover border cursor-pointer hover:opacity-80 ${photo.isInvalid ? 'border-status-fail opacity-60' : 'border-gray-200 dark:border-gray-600'}`}
                                onClick={() => window.open(photo.uri, '_blank')}
                              />
                              {photo.isInvalid && (
                                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-status-fail/20">
                                  <span className="rounded bg-status-fail px-1 py-0.5 text-[9px] font-bold text-white">INVALID</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 flex flex-col gap-2">
                              {photo.vehicleValidation && (
                                <div className={`rounded-lg border p-2 ${photo.isInvalid ? 'border-status-fail/30 bg-status-fail/5 dark:bg-status-fail/10' : 'border-green-100 dark:border-green-900/30 bg-green-50 dark:bg-green-900/20'}`}>
                                  <p className={`text-[10px] font-bold uppercase ${photo.isInvalid ? 'text-status-fail' : 'text-green-600 dark:text-green-400'}`}>
                                    {photo.isInvalid ? '⚠ Different Vehicle Detected' : '✓ Vehicle Verified'}
                                  </p>
                                  <p className="mt-0.5 text-[10px] text-gray-600 dark:text-gray-400">{photo.vehicleValidation.reasoning}</p>
                                  <p className="mt-0.5 text-[10px] text-gray-400">{Math.round(photo.vehicleValidation.confidence * 100)}% confidence</p>
                                </div>
                              )}
                              {photo.aiAnalysis && !photo.isInvalid && (
                                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-700 p-2">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${photo.aiAnalysis.severity === 'severe' ? 'bg-status-fail/10 text-status-fail' : photo.aiAnalysis.severity === 'moderate' ? 'bg-status-warning/10 text-status-warning' : photo.aiAnalysis.severity === 'minor' ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'}`}>
                                      {photo.aiAnalysis.hasDamage ? photo.aiAnalysis.severity : 'No damage'}
                                    </span>
                                    <span className="text-[10px] text-gray-400">{Math.round(photo.aiAnalysis.confidence * 100)}% confidence</span>
                                  </div>
                                  <p className="text-xs text-gray-700 dark:text-gray-300">{photo.aiAnalysis.description}</p>
                                  {photo.aiAnalysis.detectedIssues.length > 0 && (
                                    <ul className="mt-1 space-y-0.5">
                                      {photo.aiAnalysis.detectedIssues.map((issue, i) => (
                                        <li key={i} className="text-[10px] text-gray-500 dark:text-gray-400">• {issue}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">All Checks</h3>
            <div className="flex flex-col gap-4">
              {inspection.categories.map(cat => (
                <div key={cat.id}>
                  <p className="mb-2 text-sm font-semibold text-wire3-navy dark:text-wire3-green">{cat.name}</p>
                  <div className="flex flex-col gap-1">
                    {cat.checks.map(check => (
                      <div key={check.id} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{check.label}</span>
                        <span className={`text-xs font-semibold uppercase ${check.status === 'pass' ? 'text-status-pass' : check.status === 'fail' ? 'text-status-fail' : 'text-gray-400'}`}>
                          {check.status ?? '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {inspection.overallNotes && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Overall Notes</h3>
              <p className="rounded-lg bg-gray-50 dark:bg-gray-700 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                {inspection.overallNotes}
              </p>
            </section>
          )}

          {inspection.vehiclePhotos && inspection.vehiclePhotos.length > 0 && (
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Vehicle Photos</h3>
              <div className="flex flex-col gap-4">
                {inspection.vehiclePhotos.filter(vp => vp.photo !== null).map(vp => (
                  <div key={vp.promptId} className="flex gap-3">
                    <div className="relative shrink-0">
                      <img
                        src={vp.photo!.uri}
                        alt={vp.promptId}
                        className={`h-24 w-24 rounded-lg object-cover border cursor-pointer hover:opacity-80 ${vp.photo!.isInvalid ? 'border-status-fail opacity-60' : 'border-gray-200 dark:border-gray-600'}`}
                        onClick={() => window.open(vp.photo!.uri, '_blank')}
                      />
                      {vp.photo!.isInvalid && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-status-fail/20">
                          <span className="rounded bg-status-fail px-1 py-0.5 text-[9px] font-bold text-white">INVALID</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <p className="text-sm font-semibold text-wire3-navy dark:text-wire3-green capitalize">
                        {vp.promptId.replace(/-/g, ' ')}
                      </p>
                      {vp.photo!.vehicleValidation && vp.photo!.isInvalid && (
                        <div className="rounded-lg border border-status-fail/20 bg-status-fail/5 dark:bg-status-fail/10 p-2">
                          <p className="text-[10px] font-bold text-status-fail">⚠ Different Vehicle</p>
                          <p className="text-[10px] text-gray-600 dark:text-gray-400">{vp.photo!.vehicleValidation.reasoning}</p>
                        </div>
                      )}
                      {vp.photo!.aiAnalysis && (
                        <div className={`rounded-lg border p-2 ${vp.photo!.aiAnalysis.severity === 'severe' ? 'border-status-fail/20 bg-status-fail/5 dark:bg-status-fail/10' : vp.photo!.aiAnalysis.hasDamage ? 'border-status-warning/20 bg-status-warning/5 dark:bg-status-warning/10' : 'border-status-pass/20 bg-status-pass/5 dark:bg-status-pass/10'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-bold uppercase ${vp.photo!.aiAnalysis.severity === 'severe' ? 'text-status-fail' : vp.photo!.aiAnalysis.hasDamage ? 'text-status-warning' : 'text-status-pass'}`}>
                              {vp.photo!.aiAnalysis.hasDamage ? vp.photo!.aiAnalysis.severity : 'No damage'}
                            </span>
                            <span className="text-[10px] text-gray-400">{Math.round(vp.photo!.aiAnalysis.confidence * 100)}% confidence</span>
                          </div>
                          <p className="text-xs text-gray-700 dark:text-gray-300">{vp.photo!.aiAnalysis.description}</p>
                          {vp.photo!.aiAnalysis.detectedIssues.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {vp.photo!.aiAnalysis.detectedIssues.map((issue, i) => (
                                <li key={i} className="text-[10px] text-gray-500 dark:text-gray-400">• {issue}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      {vp.photo!.comparisonNotes?.hasChanges && (
                        <div className={`rounded-lg border p-2 mt-1 ${vp.photo!.comparisonNotes.severity === 'significant' ? 'border-status-fail/20 bg-status-fail/5' : vp.photo!.comparisonNotes.severity === 'moderate' ? 'border-status-warning/20 bg-status-warning/5' : 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20'}`}>
                          <p className={`text-[10px] font-bold uppercase mb-1 ${vp.photo!.comparisonNotes.severity === 'significant' ? 'text-status-fail' : vp.photo!.comparisonNotes.severity === 'moderate' ? 'text-status-warning' : 'text-yellow-600 dark:text-yellow-400'}`}>
                            △ Changes since last inspection
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300">{vp.photo!.comparisonNotes.summary}</p>
                          {vp.photo!.comparisonNotes.changes.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {vp.photo!.comparisonNotes.changes.map((change, i) => (
                                <li key={i} className="text-[10px] text-gray-500 dark:text-gray-400">• {change}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
